import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { resolveLanguage } from '@/application/i18n/language'
import { investigationSlug } from '@/application/seo/investigation-url'
import { Badge } from '@/ui/components/common/Badge'
import { MarkdownView } from '@/ui/components/common/MarkdownView'
import { ConclusionStamp } from '@/ui/components/public/ConclusionStamp'
import { ArticleMeta } from './ArticleMeta'
import type { Investigation } from '@/domains/types'

/** Full-bleed image, kicker, huge serif headline, standfirst, byline. */
export function HomeLeadStory({ investigation }: { investigation: Investigation }) {
  const { t, i18n } = useTranslation()
  const locale = resolveLanguage(i18n.language)

  return (
    <Link
      to="/investigations/$slug"
      params={{ slug: investigationSlug(investigation, locale) }}
      className="relative block group no-underline hover:no-underline"
    >
      <div className="w-full aspect-video bg-muted overflow-hidden">
        <img
          src={investigation.imageUrl || '/logo.png'}
          alt={investigation.name}
          className={`w-full h-full transition-transform duration-300 group-hover:scale-105 ${
            investigation.imageUrl ? 'object-cover' : 'object-contain p-8 opacity-40'
          }`}
        />
      </div>

      {investigation.conclusion && investigation.conclusion !== 'completed' && (
        <ConclusionStamp
          conclusion={investigation.conclusion}
          size={96}
          opaqueBackground
          className="absolute top-4 right-4"
        />
      )}

      <div className="mt-4 px-4 space-y-3">
        <p className="text-sm md:text-base uppercase tracking-widest text-primary font-bold">
          {t('home.lead_kicker')}
        </p>
        {investigation.categoryTerms && investigation.categoryTerms.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {investigation.categoryTerms.map((term) => (
              <Badge
                key={term.id}
                variant="outline"
                className="text-xs border-primary/30 text-primary"
              >
                {term.label}
              </Badge>
            ))}
          </div>
        )}
        <h1 className="font-serif text-3xl md:text-5xl font-black text-foreground leading-tight group-hover:text-primary">
          {investigation.name}
        </h1>
        <MarkdownView
          content={investigation.description}
          compact
          className="text-base md:text-lg text-muted-foreground line-clamp-5"
        />
        <ArticleMeta investigation={investigation} showCategories={false} />
      </div>
    </Link>
  )
}
